import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserManagementApplicationPermissionWebApiHandler_getApplicationManagerForCompany,
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertyEditV2WebApiHandler_isApplicationsAccepted,
  useRentalManagerUserPropertySummaryWebApiHandler_nextSteps,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import propertyUtils from '../../../../utils/propertyUtils';

import { PERMISSION_REQUEST_TYPE } from '../../../../constants/ApplicationPermissionsConstants';

import ApplicationPermissionCard from '../../../ApplicationsPage/components/ApplicationPermissionCard';

import {
  emitApplicationsDisableFromEnabledEvent,
  emitApplicationsEnableFromDisabledEvent,
} from '../../../../analytics/events/modularListing';
import { useAuth } from '../../../AuthProvider';
import ApplicationsFormField from '../../../PostingPath/ApplicationsFormField';

import type { ChangeEvent } from 'react';
import type { DatarouterError } from '@zg-rentals/web-base';
type Props = {
  setShowAppsPermissionModal: (val: boolean) => void;
};

const ApplicationsContent = ({ setShowAppsPermissionModal }: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { data: applicationManager } =
    useRentalManagerUserManagementApplicationPermissionWebApiHandler_getApplicationManagerForCompany();

  const { data: overviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const {
    mutate: editIsApplicationsAccepted,
    isPending: isSaving,
    error: editIsApplicationsAcceptedError,
  } = useRentalManagerUserPropertyEditV2WebApiHandler_isApplicationsAccepted({
    meta: {
      context: {
        api: 'rental-manager',
        details: 'useRentalManagerUserPropertyEditV2WebApiHandler_isApplicationsAccepted',
      },
      invalidates: ['users-properties-webAnalytics-get'],
    },
  });

  const applicationsSummary = listingDetails?.applicationsSummary;
  const hasNameAndIsIdVerified = user?.firstName && user?.lastName && user?.isIdVerified;
  const initialIsApplicationsAccepted = overviewData?.isApplicationsAccepted;
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: overviewData });

  const [permissionRequestType, setPermissionRequestType] = useState<keyof typeof PERMISSION_REQUEST_TYPE | null>(null);
  const [radioValue, setRadioValue] = useState<string>(
    initialIsApplicationsAccepted === null || initialIsApplicationsAccepted === undefined
      ? ''
      : String(initialIsApplicationsAccepted),
  );

  const handleSetApplicationManagerClick = () => {
    if (
      applicationsSummary?.userManagementFlags?.isEligible &&
      !applicationManager &&
      user?.isPropertyManagerCompanyAdmin
    ) {
      setShowAppsPermissionModal(true);
    }
  };

  useEffect(() => {
    if (
      initialIsApplicationsAccepted !== null &&
      initialIsApplicationsAccepted !== undefined &&
      radioValue !== String(initialIsApplicationsAccepted)
    ) {
      setRadioValue(String(initialIsApplicationsAccepted));
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [initialIsApplicationsAccepted]);

  useEffect(() => {
    if (applicationsSummary && applicationsSummary?.userManagementFlags?.isEligible) {
      const {
        userManagementFlags: { canRequestAccess, canEnableApplications, isApplicationPermissionsEnabledForCompany },
      } = applicationsSummary;

      // if a user is a company admin or has been granted permission, isIdVerified, and has entered a first and last name. The checks for name and isIdverified is so that people spoof enabling applications by adding a queryParam of verifyIdentitySuccess to enable applications w/o being verified
      if (isApplicationPermissionsEnabledForCompany && canEnableApplications && hasNameAndIsIdVerified) {
        return setPermissionRequestType(null);
      }

      if (!canEnableApplications && !canRequestAccess) {
        setPermissionRequestType(PERMISSION_REQUEST_TYPE.NO_PERMISSION);
      }

      if (isApplicationPermissionsEnabledForCompany) {
        if (canRequestAccess) {
          // company admin has already enabled applications for listing, user just needs to request permission to be able to view them
          setPermissionRequestType(PERMISSION_REQUEST_TYPE.REQUEST_ACCESS);
        }
      } else if (canEnableApplications) {
        // user is a company admin and has the power to enable applications for the listing. The company admin will become an application manager and be able to grand permission to other users to see apps
        setPermissionRequestType(PERMISSION_REQUEST_TYPE.SET_APPLICATION_MANAGER);
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [applicationsSummary]);

  const handleRadioChange = (event: ChangeEvent<Element>) => {
    const newValue = (event.target as HTMLInputElement).value;
    setRadioValue(newValue);

    const prevIsApplicationsAccepted = initialIsApplicationsAccepted;
    const nextValue = newValue === 'true';

    const params = {
      propertyId: listingAlias,
      isApplicationsAccepted: nextValue,
      inviteOnly: false,
    };

    editIsApplicationsAccepted(
      { postData: params },
      {
        onSuccess: () => {
          const csParams = { currentProperty: { listingAlias }, user: user ?? {} };

          if (nextValue && prevIsApplicationsAccepted === false) {
            emitApplicationsEnableFromDisabledEvent({
              ...csParams,
              triggerLocation: 'zrm_listing_page_review',
            });
          } else if (!nextValue && prevIsApplicationsAccepted === true) {
            emitApplicationsDisableFromEnabledEvent({
              ...csParams,
              triggerLocation: 'zrm_listing_page_review',
            });
          }

          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({
              listingAlias,
            }),
          });
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertySummaryWebApiHandler_nextSteps.getKey({
              listingAlias,
            }),
          });
        },
        onError: () => {
          setRadioValue(String(prevIsApplicationsAccepted));
        },
      },
    );
  };

  return (
    <>
      {permissionRequestType ? (
        <ApplicationPermissionCard
          listingAlias={listingAlias}
          applicationsSummary={applicationsSummary}
          permissionType={permissionRequestType}
          onSetApplicationManagerClick={handleSetApplicationManagerClick}
        />
      ) : (
        <ApplicationsFormField
          isIntroModal={false}
          isRoomForRent={isRoomForRent}
          radioValue={radioValue}
          onRadioChange={handleRadioChange}
          serverError={(editIsApplicationsAcceptedError as DatarouterError)?.message ?? ''}
          disabled={isSaving}
        />
      )}
    </>
  );
};

export default ApplicationsContent;
